/**
 * The shared filterable list (decision 101, #401): what a list can be filtered by.
 *
 * A field has one value at a time, which is what every list read in the console accepts today
 * (`?status=pending`). Finite options carry the label the person reads; the value goes in the URL.
 * A monthPicker field passes `options: []` and selects a month/year without finite options.
 */
export interface FilterOption {
  readonly value: string;
  readonly label: string;
  /** Shown indented, under the option before it (a supplier type's detail under its macro). */
  readonly indent?: boolean;
}

export interface FilterField {
  /** The URL parameter, e.g. `status`. */
  readonly key: string;
  /** What the field is called on screen, e.g. «Stato». */
  readonly label: string;
  readonly options: readonly FilterOption[];
  /** A persistent month/year picker instead of finite options. */
  readonly monthPicker?: boolean;
  /**
   * Several values at once, comma separated in the URL (`type=food,taxi`): the menu offers
   * checkboxes and stays open while they are ticked.
   */
  readonly multiple?: boolean;
}

/** The values a field holds: one, or for a `multiple` field each of its comma-separated ones. */
export function fieldValues(field: FilterField, value: string | null | undefined): string[] {
  if (!value) return [];
  if (!field.multiple) return [value];
  return value
    .split(',')
    .map((one) => one.trim())
    .filter(Boolean);
}

/** The label of the value a field holds, or the raw value when no option matches it. */
export function optionLabel(field: FilterField, value: string): string {
  const label = (one: string) => field.options.find((option) => option.value === one)?.label ?? one;
  return field.multiple ? fieldValues(field, value).map(label).join(', ') : label(value);
}
